import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

export default function ITPoliciesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    code: "",
    name: "",
    description: "",
    is_active: 1,
    reason: "",
  });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableQ, setAvailableQ] = useState("");
  const [selectedPermissionId, setSelectedPermissionId] = useState("");
  const [permissionReason, setPermissionReason] = useState("");

  useEffect(() => {
    loadPolicies();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadPolicies();
    }, 250);
    return () => clearTimeout(t);
  }, [q, statusFilter]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await accessApi.getPolicies({
        q: q || undefined,
        is_active: statusFilter === "" ? undefined : statusFilter,
      });

      setRows(res?.data?.data || []);
    } catch (err) {
      console.error("ITPoliciesPage load error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load policies."
      );
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingRow(null);
    setPolicyForm({
      code: "",
      name: "",
      description: "",
      is_active: 1,
      reason: "",
    });
    setShowPolicyModal(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setPolicyForm({
      code: row.code || "",
      name: row.name || "",
      description: row.description || "",
      is_active: Number(row.is_active) === 1 ? 1 : 0,
      reason: "",
    });
    setShowPolicyModal(true);
  };

  const closePolicyModal = () => {
    if (saving) return;
    setShowPolicyModal(false);
    setEditingRow(null);
  };

  const savePolicy = async () => {
    try {
      if (!policyForm.code.trim()) {
        alert("Code is required.");
        return;
      }
      if (!policyForm.name.trim()) {
        alert("Name is required.");
        return;
      }

      setSaving(true);

      const payload = {
        code: policyForm.code.trim(),
        name: policyForm.name.trim(),
        description: policyForm.description?.trim() || null,
        is_active: Number(policyForm.is_active) === 1 ? 1 : 0,
        reason: policyForm.reason?.trim() || null,
      };

      if (editingRow?.id) {
        await accessApi.updatePolicy(editingRow.id, payload);
      } else {
        await accessApi.createPolicy(payload);
      }

      setShowPolicyModal(false);
      setEditingRow(null);
      await loadPolicies();
    } catch (err) {
      console.error("savePolicy error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to save policy."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await accessApi.updatePolicyStatus(row.id, {
        is_active: Number(row.is_active) === 1 ? 0 : 1,
        reason:
          Number(row.is_active) === 1
            ? "Deactivated from IT Admin"
            : "Activated from IT Admin",
      });

      await loadPolicies();

      if (selectedPolicy?.id === row.id) {
        await openDetail(row.id);
      }
    } catch (err) {
      console.error("toggleStatus error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update policy status."
      );
    }
  };

  const openDetail = async (policyId) => {
    try {
      setShowDetailModal(true);
      setDetailLoading(true);
      setDetailError("");
      setSelectedPolicy(null);

      const res = await accessApi.getPolicyDetail(policyId);
      setSelectedPolicy(res?.data?.data || null);
    } catch (err) {
      console.error("openDetail error:", err);
      setDetailError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load policy detail."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPolicy(null);
    setDetailError("");
    setAvailablePermissions([]);
    setSelectedPermissionId("");
    setPermissionReason("");
    setAvailableQ("");
    setShowAddPermissionModal(false);
  };

  const loadAvailablePermissions = async (policyId) => {
    try {
      setAvailableLoading(true);

      const res = await accessApi.getAvailablePermissionsForPolicy(policyId, {
        q: availableQ || undefined,
        is_active: 1,
      });

      const data = res?.data?.data || [];
      setAvailablePermissions(data);

      if (data.length && !selectedPermissionId) {
        setSelectedPermissionId(String(data[0].id));
      }
    } catch (err) {
      console.error("loadAvailablePermissions error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load available permissions."
      );
    } finally {
      setAvailableLoading(false);
    }
  };

  const openAddPermissionModal = async () => {
    if (!selectedPolicy?.id) return;
    setShowAddPermissionModal(true);
    setAvailableQ("");
    setSelectedPermissionId("");
    setPermissionReason("");
    await loadAvailablePermissions(selectedPolicy.id);
  };

  useEffect(() => {
    if (!showAddPermissionModal || !selectedPolicy?.id) return;

    const t = setTimeout(() => {
      loadAvailablePermissions(selectedPolicy.id);
    }, 250);

    return () => clearTimeout(t);
  }, [availableQ, showAddPermissionModal, selectedPolicy?.id]);

  const addPermissionToPolicy = async () => {
    try {
      if (!selectedPolicy?.id) return;
      if (!selectedPermissionId) {
        alert("Select permission.");
        return;
      }

      await accessApi.addPolicyPermission(selectedPolicy.id, {
        permission_id: Number(selectedPermissionId),
        reason: permissionReason?.trim() || null,
      });

      setShowAddPermissionModal(false);
      setAvailablePermissions([]);
      setSelectedPermissionId("");
      setPermissionReason("");

      await openDetail(selectedPolicy.id);
      await loadPolicies();
    } catch (err) {
      console.error("addPermissionToPolicy error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to add permission to policy."
      );
    }
  };

  const removePermissionFromPolicy = async (mappingId) => {
    try {
      if (!selectedPolicy?.id) return;

      const confirmed = window.confirm(
        "Remove this permission from the policy?"
      );
      if (!confirmed) return;

      await accessApi.removePolicyPermission(mappingId, {
        reason: "Removed from IT Admin policy editor",
      });

      await openDetail(selectedPolicy.id);
      await loadPolicies();
    } catch (err) {
      console.error("removePermissionFromPolicy error:", err);
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to remove permission from policy."
      );
    }
  };

  const tableRows = useMemo(() => rows || [], [rows]);

  if (loading && !tableRows.length && !error) {
    return (
      <div className="p-6 text-black dark:text-white">Loading policies...</div>
    );
  }

  if (error && !tableRows.length) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Policies
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage policy bundles used across the platform.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
        >
          + Add Policy
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search policies..."
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">All statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Code
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Name
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Description
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Permissions
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Status
                </th>
                <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 text-black dark:text-white font-medium">
                    {row.code}
                  </td>
                  <td className="p-3 text-black dark:text-white">{row.name}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">
                    {row.description || "-"}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {row.permissions_count ?? 0}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {Number(row.is_active) === 1 ? "Active" : "Inactive"}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEdit(row)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => toggleStatus(row)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        {Number(row.is_active) === 1
                          ? "Deactivate"
                          : "Activate"}
                      </button>

                      <button
                        onClick={() => openDetail(row.id)}
                        className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                      >
                        Manage Permissions
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {tableRows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    No policies found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPolicyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-black dark:text-white">
                {editingRow ? "Edit Policy" : "Add Policy"}
              </h2>
              <button
                onClick={closePolicyModal}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Code
                </label>
                <input
                  value={policyForm.code}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  disabled={!!editingRow}
                  placeholder="e.g. policy_ar_user"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white disabled:opacity-60"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Name
                </label>
                <input
                  value={policyForm.name}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. AR User"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Status
                </label>
                <select
                  value={policyForm.is_active}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({
                      ...prev,
                      is_active: Number(e.target.value),
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div />

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Description
                </label>
                <textarea
                  value={policyForm.description}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 text-black dark:text-white">
                  Reason
                </label>
                <input
                  value={policyForm.reason}
                  onChange={(e) =>
                    setPolicyForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  placeholder="Optional audit reason"
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closePolicyModal}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Cancel
              </button>
              <button
                onClick={savePolicy}
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-6xl rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 p-6 space-y-4 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-black dark:text-white">
                  Policy Permissions
                </h2>
                {selectedPolicy ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectedPolicy.name} ({selectedPolicy.code})
                  </p>
                ) : null}
              </div>

              <button
                onClick={closeDetailModal}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            {detailLoading ? (
              <div className="text-black dark:text-white">
                Loading policy detail...
              </div>
            ) : detailError ? (
              <div className="text-red-600 dark:text-red-400">{detailError}</div>
            ) : selectedPolicy ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Code
                    </div>
                    <div className="mt-1 text-black dark:text-white font-medium">
                      {selectedPolicy.code}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Name
                    </div>
                    <div className="mt-1 text-black dark:text-white font-medium">
                      {selectedPolicy.name}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Status
                    </div>
                    <div className="mt-1 text-black dark:text-white font-medium">
                      {Number(selectedPolicy.is_active) === 1
                        ? "Active"
                        : "Inactive"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      Permissions
                    </div>
                    <div className="mt-1 text-black dark:text-white font-medium">
                      {selectedPolicy.permissions_count ?? 0}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
                    Description
                  </div>
                  <div className="mt-1 text-black dark:text-white">
                    {selectedPolicy.description || "-"}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={openAddPermissionModal}
                    className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
                  >
                    + Add Permission
                  </button>
                </div>

                <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[1000px]">
                      <thead className="bg-gray-50 dark:bg-neutral-800">
                        <tr className="border-b border-gray-200 dark:border-neutral-700">
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Code
                          </th>
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Name
                          </th>
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Module
                          </th>
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Description
                          </th>
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Status
                          </th>
                          <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-200">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedPolicy.permissions || []).map((perm) => (
                          <tr
                            key={perm.mapping_id}
                            className="border-b border-gray-100 dark:border-neutral-800"
                          >
                            <td className="p-3 text-black dark:text-white font-medium">
                              {perm.code}
                            </td>
                            <td className="p-3 text-black dark:text-white">
                              {perm.name}
                            </td>
                            <td className="p-3 text-black dark:text-white">
                              <div className="font-medium">
                                {perm.module_name || "-"}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {perm.module_code}
                              </div>
                            </td>
                            <td className="p-3 text-gray-700 dark:text-gray-300">
                              {perm.description || "-"}
                            </td>
                            <td className="p-3 text-black dark:text-white">
                              {Number(perm.is_active) === 1
                                ? "Active"
                                : "Inactive"}
                            </td>
                            <td className="p-3">
                              <button
                                onClick={() =>
                                  removePermissionFromPolicy(perm.mapping_id)
                                }
                                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}

                        {!selectedPolicy.permissions?.length && (
                          <tr>
                            <td
                              colSpan={6}
                              className="p-8 text-center text-gray-500 dark:text-gray-400"
                            >
                              No permissions assigned to this policy.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showAddPermissionModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 p-6 space-y-4 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-black dark:text-white">
                Add Permission to Policy
              </h3>
              <button
                onClick={() => setShowAddPermissionModal(false)}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <input
              value={availableQ}
              onChange={(e) => setAvailableQ(e.target.value)}
              placeholder="Search available permissions..."
              className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            />

            {availableLoading ? (
              <div className="text-black dark:text-white">
                Loading available permissions...
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden">
                <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-gray-50 dark:bg-neutral-800 sticky top-0">
                      <tr className="border-b border-gray-200 dark:border-neutral-700">
                        <th className="text-left p-3">Select</th>
                        <th className="text-left p-3">Code</th>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Module</th>
                        <th className="text-left p-3">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availablePermissions.map((perm) => (
                        <tr
                          key={perm.id}
                          className="border-b border-gray-100 dark:border-neutral-800"
                        >
                          <td className="p-3">
                            <input
                              type="radio"
                              name="selectedPermission"
                              checked={String(selectedPermissionId) === String(perm.id)}
                              onChange={() => setSelectedPermissionId(String(perm.id))}
                            />
                          </td>
                          <td className="p-3 text-black dark:text-white font-medium">
                            {perm.code}
                          </td>
                          <td className="p-3 text-black dark:text-white">
                            {perm.name}
                          </td>
                          <td className="p-3 text-black dark:text-white">
                            <div className="font-medium">
                              {perm.module_name || "-"}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {perm.module_code}
                            </div>
                          </td>
                          <td className="p-3 text-gray-700 dark:text-gray-300">
                            {perm.description || "-"}
                          </td>
                        </tr>
                      ))}

                      {!availablePermissions.length && (
                        <tr>
                          <td
                            colSpan={5}
                            className="p-8 text-center text-gray-500 dark:text-gray-400"
                          >
                            No available permissions found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm mb-1 text-black dark:text-white">
                Reason
              </label>
              <input
                value={permissionReason}
                onChange={(e) => setPermissionReason(e.target.value)}
                placeholder="Optional audit reason"
                className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAddPermissionModal(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Cancel
              </button>
              <button
                onClick={addPermissionToPolicy}
                className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
              >
                Add Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}